import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TREATMENTS } from "@/lib/studio";
import { EASE } from "@/lib/scroll";
import { Reveal, Eyebrow } from "./Reveal";

const Frame = ({ t, tall }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.15 }}
    className={`relative ${tall ? "aspect-[1206/1168]" : "aspect-[4/3] lg:aspect-[16/9]"}`}
  >
    <div className="absolute inset-0 overflow-hidden rounded-tl-[56px] rounded-br-[56px] bg-champagne">
      <motion.img
        variants={{ hidden: { scale: 1.18 }, show: { scale: 1 } }}
        transition={{ duration: 1.6, ease: EASE }}
        src={t.img}
        alt={t.alt}
        loading="lazy"
        style={{ objectPosition: t.pos || "50% 50%" }}
        className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-ink/35 via-transparent to-transparent" />
    </div>
    <span className="absolute left-5 top-4 font-serif text-5xl italic text-cream drop-shadow-[0_2px_10px_rgba(45,36,30,0.35)]">{t.numeral}</span>
  </motion.div>
);

const Card = ({ t, tall, onSelect, delay }) => (
  <Reveal delay={delay} className={`group flex h-full flex-col rounded-[28px] border border-beige bg-paper p-3 shadow-[0_10px_40px_rgba(45,36,30,0.05)] transition-shadow duration-500 hover:shadow-[0_20px_60px_rgba(197,155,39,0.16)] ${tall ? "lg:row-span-2" : ""}`}>
    <div data-testid={`treatment-card-${t.id}`} className="flex h-full flex-col">
      <Frame t={t} tall={tall} />
      <div className="flex flex-1 flex-col px-3 pb-3 pt-6 sm:px-5">
        <h3 className="font-serif text-3xl text-ink sm:text-[2.1rem]">{t.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">{t.text}</p>
        <ul className="mt-5 space-y-2">
          {t.points.map((p) => (
            <li key={p} className="flex gap-3 text-sm text-ink-soft">
              <span className="mt-2 h-px w-4 shrink-0 bg-gold" />
              {p}
            </li>
          ))}
        </ul>
        <button
          data-testid={`treatment-request-${t.id}`}
          onClick={() => onSelect(t.title)}
          className="mt-auto inline-flex items-center gap-2 self-start pt-6 text-[12px] font-semibold uppercase tracking-[0.22em] text-gold-deep"
        >
          <span className="link-underline">Diese Behandlung anfragen</span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  </Reveal>
);

export const Treatments = ({ onSelect }) => (
  <section id="behandlungen" data-testid="treatments-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-36">
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-end">
      <Reveal>
        <Eyebrow index="01">Meine Behandlungen</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
          Pflege, die man <span className="italic text-gold-deep">bei jedem Schritt</span> spürt.
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="max-w-md text-[15px] leading-relaxed text-ink-soft lg:justify-self-end">
        Jede Behandlung beginnt mit einem persönlichen Gespräch – damit deine Pflege genau zu dir und deinen Füßen passt.
      </Reveal>
    </div>
    <div className="mt-14 grid gap-6 lg:mt-20 lg:grid-cols-12 lg:grid-rows-2">
      <div className="lg:col-span-7 lg:row-span-2">
        <Card t={TREATMENTS[0]} tall onSelect={onSelect} delay={0} />
      </div>
      <div className="lg:col-span-5">
        <Card t={TREATMENTS[1]} onSelect={onSelect} delay={0.1} />
      </div>
      <div className="lg:col-span-5">
        <Card t={TREATMENTS[2]} onSelect={onSelect} delay={0.2} />
      </div>
    </div>
  </section>
);
